import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./../../../../assets/css/PrescriptionCardItems.scss";
import SearchableDropdown from "../../../../components/AddPrescriptionItem/SearchableDropdown";
import { charLimitMessage } from "../../../../lib/utils";
import useTitanApp from "../../../../hooks/useTitanApp";
import {
  addPrescription,
  getDrugInformation,
  setDrugsAboutData,
  getProductByPrescriptionId,
} from "../../../../dispatchers/nmsDispatchers";
import { showPrescriptionSendError } from "../../../../components/PrescriptionSendErrorPopup";
import { GoPlus } from "react-icons/go";
import { v4 as uuidv4 } from "uuid";
import { generatePrescriptionImageBase64 } from "./generatePrescriptionImage";
import config from "../../../../config";
import { ActionType } from "../../../../enum/ActionType";
import removeActionFromLocal from "../../../../utils/removeFromLocalStorage.js";
import {
  updateConsultationActions,
  setIncrementPrescriptionCompleted,
  setShowPrescriptionPreviewModal,
  setPrescriptionImageBase64,
  setPrescriptionDetails,
  setDynamicPrescriptions,
  setPrescriptionId,
  setSelectedPrescriptionCategory,
  trackServiceBilling,
  setAddPatientAIChatMessage,
  SaveAIChatMessage,
  deletePrescriptionDraft,
  getPatientMedications,
} from "../../../../dispatchers/patientDispatchers";
import { IoClose } from "react-icons/io5";
import SendToPharmacy from "../../../../components/PatientConsultation/PatientConsultationUtils/SendToPharmacy";
import { BsTrash3 } from "react-icons/bs";
import CustomPrescriptionTypeDropdown from "../../../../components/PatientConsultation/PatientConsultationUtils/CustomPrescriptionTypeDropdown";
import {
  AlertTriangle,
  CheckCircle,
  Eye,
  EyeOff,
  Hash,
  Key,
  Shield,
} from "lucide-react";
import { Label } from "recharts";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { getUserCertificate } from "../../../../dispatchers/onboardingVerificationDsipatchers.js";
import { usePrescriptionData } from "@/lib/prescriptionData";
import { setConsultationLoadError } from "../../../../dispatchers/appDispatchers";
import { Source } from "react-map-gl";
import { trackFirstTimeIntercomEvent } from "@/utils/trackFirstTimeIntercomEvent";
import { addPharmacyNotification } from "../../../../dispatchers/pharmacyNotificationsDispatchers.js";
import { getPharmacyContactDetails } from "../../../../dispatchers/pharmacyServiceDispatchers";

const WORKFLOW_STEPS = {
  SELECTION: 1,
  PHARMACY: 2,
  SUCCESS: 3,
  SIGNING: 4,
};

// Validation schema
const prescriptionItemSchema = Yup.object().shape({
  value: Yup.string().required("Medication is required*"),
  dose: Yup.string()
    .required("Dosage is required*")
    .max(500, "Dosage cannot exceed 500 characters"),
  quantity: Yup.string().required("Quantity is required*"),
  notes: Yup.string().max(
    1000,
    "Additional notes cannot exceed 1000 characters",
  ),
});

const PrescriptionCardItems = ({ handleRemove, handleClose }) => {
  const {
    appState,
    nmsServiceState,
    patientState,
    pharmacyServiceState,
    memberState,
  } = useTitanApp();
  const {
    selectedPatientId,
    selectedPatientDetails,
    consultationId,
    patientPrescription,
    dynamicPrescriptions,
    selectedPatientConsultation,
    prescriptionId,
    isPrescriptionUpdate,
    selectedPrescriptionCategory,
    prescriptionCompleted,
    patientAIChatMessages,
    consultationData,
  } = patientState || {};
  const { userProfiles } = memberState || {};
  const userDigitalCertificate =
    userProfiles?.digitalSignatures?.userCertificate;
  const {
    darkMode,
    currentPharmacy,
    currentUser,
    company,
    currentPharmacyId,
    companyId,
  } = appState || {};
  const { doseShortCodeList } = pharmacyServiceState || {};
  const [sendToPharmacyID, setSendToPharmacyID] = useState("");
  const [sendToPharmacy, setSendToPharmacy] = useState("");
  const [drugsInfoMap, setDrugsInfoMap] = useState({});
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [showErrors, setShowErrors] = useState(false); // New state to control when to show errors
  const [pharmacyError, setPharmacyError] = useState("");
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [workflowStep, setWorkflowStep] = React.useState(
    WORKFLOW_STEPS.SELECTION,
  );
  // const [selectedPrescriptionCategory, setSelectedPrescriptionCategory] =
  //   useState("private");
  const [prescriptionType, setPrescriptionType] = useState();
  const [prescriptionError, setPrescriptionError] = useState("");
  const [isContinueDisabled, setIsContinueDisabled] = useState(true);
  const { appointmentApp } = config;
  const isConsultationCompleted =
    selectedPatientConsultation?.consultationStatus === 1;
  const isDisabled =
    isConsultationCompleted || localStorage.getItem("patientInfoTab") === 0;
  // PGD supplies (e.g. AHT Weight Management) are not technically
  // prescriptions, so the UI should refer to them as "PGD Supply" rather
  // than "Prescription". selectedPrescriptionCategory is set to "pgd" by
  // PrescriptionModal/index.tsx's defaultCategory prop when opened from a
  // true PGD pathway — mirrors the same flag/wording Builder's
  // PrescriptionModal.tsx uses.
  const isPgdSupply = selectedPrescriptionCategory === "pgd";
  const supplyNounTitle = isPgdSupply ? "PGD Supply" : "Prescription";
  const supplyNoun = isPgdSupply ? "PGD supply" : "prescription";
  const [
    isGenerateForApprovalLoading,
    setIsGenerateForApprovalLoading,
  ] = useState(false);
  const newPrescriptionId = uuidv4();

  useEffect(() => {
    const { isValid } = validateAllItems();
    const isPrescriptionCategoryValid =
      selectedPrescriptionCategory?.trim?.() !== "";
    const isPrescriptionTypeValid = prescriptionType?.trim?.() !== "";

    setIsContinueDisabled(
      !isValid || !isPrescriptionCategoryValid || !isPrescriptionTypeValid,
    );
  }, [dynamicPrescriptions, selectedPrescriptionCategory, prescriptionType]);

  useEffect(() => {
    if (isPrescriptionUpdate && dynamicPrescriptions.length > 0) {
      const itemsNeedIds = dynamicPrescriptions.some((item) => !item.id);
      if (itemsNeedIds) {
        const fixedData = dynamicPrescriptions.map((item) => ({
          ...item,
          id: item.id || uuidv4(),
        }));
        setDynamicPrescriptions(fixedData);
      }
    }
  }, [isPrescriptionUpdate, dynamicPrescriptions]);

  useEffect(() => {
    const fetchDescriptions = async () => {
      let hasUpdates = false;
      const updatedPrescriptions = await Promise.all(
        dynamicPrescriptions.map(async (item) => {
          if (item?.prescribedItemId && !item?.isDescriptionFetched) {
            try {
              const res = await getProductByPrescriptionId(
                item.prescribedItemId,
              );
              const drugDescription = res?.description || res?.Description;
              hasUpdates = true;
              return {
                ...item,
                value: drugDescription || "",
                isDescriptionFetched: true,
              };
            } catch (error) {
              console.error("Error fetching product description:", error);
              hasUpdates = true;
              return { ...item, value: "", isDescriptionFetched: true };
            }
          }
          return item;
        }),
      );

      if (hasUpdates) {
        setDynamicPrescriptions(updatedPrescriptions);
      }
    };

    if (dynamicPrescriptions?.length > 0) {
      const needsFetch = dynamicPrescriptions.some(
        (item) => item?.prescribedItemId && !item?.isDescriptionFetched,
      );
      if (needsFetch) {
        fetchDescriptions();
      }
    }
  }, [dynamicPrescriptions]);

  const handleProceedWithIssue = () => {
    if (isDisabled) return;
    // Validate prescription type before proceeding
    if (!selectedPrescriptionCategory) {
      setPrescriptionError("Please select a prescription type*");
      return;
    }

    const { isValid, errorsMap } = validateAllItems();
    if (!isValid) {
      setShowErrors(true);
      return;
    }

    setPrescriptionError("");
    setShowErrors(false);
    setWorkflowStep(WORKFLOW_STEPS.SIGNING);
  };

  const handleGenerateForApproval = async () => {
    if (isDisabled) return;
    // Validate prescription type before proceeding
    setIsGenerateForApprovalLoading(true);
    if (!selectedPrescriptionCategory) {
      setPrescriptionError("Please select a prescription type*");
      setIsGenerateForApprovalLoading(false);
      return;
    }

    const { isValid, errorsMap } = validateAllItems();
    if (!isValid) {
      setShowErrors(true);
      setIsGenerateForApprovalLoading(false);
      return;
    }

    // Create the payload here
    const Drugs = dynamicPrescriptions?.map((item) => ({
      prescriptionItemId: item?.prescribedItemId,
      drugName: item?.value,
      strength: item?.strength,
      medicationType: item?.medicationType,
      quantity: Number(item?.quantity),
      dose: item?.dose,
      note: item?.notes,
    }));

    const result = await getHtmlData(Drugs, false);
    const { base64Image, expiryDate } = result;

    const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
    const prescriptionModel = {
      prescriptionId: isPrescriptionUpdate ? prescriptionId : newPrescriptionId,
      patientId: selectedPatientId,
      pharmacyID: EMPTY_GUID,
      drugs: Drugs,
    };
    const prescriptionCategory = selectedPrescriptionCategory;
    const payload = {
      prescriptionDetails: prescriptionModel,
      isPrescriptionGenerated: false, // For approval
      consultationID: consultationId,
      prescriptionCategory:
        prescriptionCategory === "private"
          ? 1
          : prescriptionCategory === "pgd"
          ? 2
          : 3,
    };

    await savePrescription(false, true);
    const notificationPayload = {
      pharmacyId: currentPharmacyId,
      notificationStatusType: 3,
      title: "Prescription Approval Required",
      notificationMessageType: 5,
      entityId: isPrescriptionUpdate ? prescriptionId : newPrescriptionId,
    };
    await addPharmacyNotification(notificationPayload);

    handleActionSave(base64Image, payload);

    setIsGenerateForApprovalLoading(false);
    setPrescriptionError("");
    setShowErrors(false);
    setWorkflowStep(WORKFLOW_STEPS.SUCCESS);
  };

  const handleActionSave = async (base64Image, payload) => {
    try {
      const {
        prescriptionDetails: { base64Image: _, ...prescriptionDetailsClean },
        ...restPayload
      } = payload;

      const cleanPayload = {
        ...restPayload,
        prescriptionDetails: prescriptionDetailsClean,
      };
      const prescriptionPayload = {
        payload: JSON.stringify({
          ...cleanPayload,
          recordedAt: new Date().toISOString(),
        }),
        actionName: ActionType[1],
        actionNumber: "1",
        isPerformed: true,
        image: base64Image,
      };

      const prescriptionPayloadForchat = {
        payload: JSON.stringify({
          ...cleanPayload,
          recordedAt: new Date().toISOString(),
        }),
        actionName: ActionType[1],
        actionNumber: "1",
        isPerformed: true,
      };

      setPrescriptionDetails(prescriptionPayloadForchat);

      await updateConsultationActions({
        consultationId: consultationId,
        payload: prescriptionPayload,
      });
      const prescriptionMessage = [
        {
          sender: "success",
          message: "Prescription completed successfully.",
          actionId: 1,
          details: prescriptionPayloadForchat,
        },
      ];
      await SaveAIChatMessage({
        ConsultationChat: JSON.stringify(prescriptionMessage),
        ConsultationId: consultationId,
      });
      setAddPatientAIChatMessage({
        sender: "success",
        message: "Prescription completed successfully.",
        actionId: 1,
        details: prescriptionPayloadForchat,
      });

      const pendingDraftFromChat = patientAIChatMessages.find(
        (msg) =>
          (msg.actionId === 11 || msg.actionId === "11") &&
          msg.sender === "success",
      );
      const pendingDraftFromActions = (consultationData?.actions || []).find(
        (action) => action.actionName === "CreatePrescriptionDraft",
      );
      const hasPendingDraft =
        !!pendingDraftFromChat || !!pendingDraftFromActions;

      if (hasPendingDraft) {
        await deletePrescriptionDraft({
          consultationID: consultationId,
          actionNumber: "11",
        });

        const completionMarker = {
          sender: "success",
          message: "Prescription draft completed.",
          actionId: 11,
          details: {
            ...(pendingDraftFromChat?.details || {}),
            draftStatus: "completed",
          },
        };

        setAddPatientAIChatMessage(completionMarker);

        await SaveAIChatMessage({
          ConsultationChat: JSON.stringify([completionMarker]),
          ConsultationId: consultationId,
        });
      }

      if (prescriptionCompleted) {
      }

      removeActionFromLocal(consultationId, prescriptionPayload.actionNumber);
    } catch (error) {
      console.error("Failed to update consultation action:", error);
      setConsultationLoadError(true);
    }
  };

  const handleRemoveClick = () => {
    handleClose();
    setDrugsAboutData([]);
    // selectedPrescriptionCategory is global state and isn't set by every
    // trigger that opens this modal (e.g. the generic "Create Prescription"
    // quick action never sets it). Reset here on close so a leftover "pgd"
    // value from an AHT consultation can't leak into the next, unrelated
    // prescription and show the wrong ("PGD Supply") wording/category.
    setSelectedPrescriptionCategory("");
  };

  const getDrugInformations = async (prescribedItemId, id) => {
    const res = await getDrugInformation(prescribedItemId);
    setDrugsInfoMap((prev) => ({
      ...prev,
      [id]: res?.drugsAboutData || [],
    }));
  };

  const addPrescriptionItems = () => {
    if (isDisabled) return;
    const newPrescription = {
      id: uuidv4(),
      value: "",
      quantity: "",
      dose: "",
      notes: "",
      prescribedItemId: "",
    };

    const updatedPrescriptions = [...dynamicPrescriptions, newPrescription];
    setDynamicPrescriptions(updatedPrescriptions);
  };

  const removePrescription = (id) => {
    if (isDisabled) return;
    const updatedPrescriptions = dynamicPrescriptions.filter(
      (item) => item?.id !== id,
    );
    setDynamicPrescriptions(updatedPrescriptions);
  };
  const numberToWords = (num) => {
    const ones = [
      "",
      "one",
      "two",
      "three",
      "four",
      "five",
      "six",
      "seven",
      "eight",
      "nine",
      "ten",
      "eleven",
      "twelve",
      "thirteen",
      "fourteen",
      "fifteen",
      "sixteen",
      "seventeen",
      "eighteen",
      "nineteen",
    ];

    const tens = [
      "",
      "",
      "twenty",
      "thirty",
      "forty",
      "fifty",
      "sixty",
      "seventy",
      "eighty",
      "ninety",
    ];

    if (num < 20) return ones[num];

    if (num < 100) {
      return `${tens[Math.floor(num / 10)]}${
        num % 10 ? " " + ones[num % 10] : ""
      }`;
    }

    if (num < 1000) {
      return `${ones[Math.floor(num / 100)]} hundred${
        num % 100 ? " " + numberToWords(num % 100) : ""
      }`;
    }

    return num.toString();
  };

  const numberToDoseText = (num) => {
    if (num === 1) return "one to be taken";
    if (num === 2) return "twice to be taken";
    if (num === 3) return "thrice to be taken";

    return `${numberToWords(num)} times to be taken`;
  };

  const handleChange = (id, field, val, item) => {
    if (isDisabled) return;

    const updatedPrescriptions = dynamicPrescriptions.map(
      (prescriptionItem) => {
        if (prescriptionItem?.id === id) {
          let valueToSet;

          if (field === "dose" && typeof val === "string") {
            const endsWithSpace = val.endsWith(" ");

            if (endsWithSpace) {
              const trimmedVal = val.slice(0, -1); // remove trailing space
              const words = trimmedVal.split(" ");
              const lastWord = words[words.length - 1];

              const matched = doseShortCodeList?.find(
                (entry) =>
                  entry?.shortCode?.toLowerCase() === lastWord.toLowerCase(),
              );

              if (matched) {
                words[words.length - 1] = matched.description;
                valueToSet = words.join(" ") + " ";
              } else {
                valueToSet = val;
              }
            } else {
              valueToSet = val;
            }
          } else if (field === "value" && val && typeof val === "object") {
            valueToSet = val?.drugName;
            return {
              ...prescriptionItem,
              [field]: valueToSet,
              prescribedItemId: val?.prescribedItemId || "",
              isDescriptionFetched: true,
            };
          } else {
            valueToSet = val;
          }

          return { ...prescriptionItem, [field]: valueToSet };
        }

        return prescriptionItem;
      },
    );

    setDynamicPrescriptions(updatedPrescriptions);

    if (val?.prescribedItemId) {
      getDrugInformations(val?.prescribedItemId, id);
    }
  };

  const validateAllItems = () => {
    let isValid = true;
    const errorsMap = {};

    dynamicPrescriptions.forEach((item) => {
      try {
        prescriptionItemSchema.validateSync(item, { abortEarly: false });
        errorsMap[item?.id] = {};
      } catch (err) {
        isValid = false;
        const itemErrors = {};
        err.inner.forEach((error) => {
          itemErrors[error.path] = error.message;
        });
        errorsMap[item?.id] = itemErrors;
      }
    });

    return { isValid, errorsMap };
  };

  const savePrescription = async (
    isPrescriptionGenerated,
    skipActionSave = false,
  ) => {
    if (isDisabled) return;
    setButtonsDisabled(true);
    const { isValid, errorsMap } = validateAllItems();
    let hasError = false;

    if (!isValid) {
      setShowErrors(true);
      hasError = true;
    } else {
      setShowErrors(false);
    }

    if (isPrescriptionGenerated && !sendToPharmacyID) {
      setPharmacyError("Please select a pharmacy*");
      hasError = true;
    } else {
      setPharmacyError("");
    }

    if (hasError) {
      return;
    }

    const Drugs = dynamicPrescriptions?.map((item) => ({
      prescriptionItemId: item?.prescribedItemId,
      drugName: item?.value,
      strength: item?.strength,
      medicationType: item?.medicationType,
      quantity: Number(item?.quantity),
      dose: item?.dose,
      note: item?.notes,
    }));

    const result = await getHtmlData(Drugs, false);

    const { base64Image, expiryDate } = result;
    const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
    const prescriptionModel = {
      prescriptionId: isPrescriptionUpdate ? prescriptionId : newPrescriptionId,
      patientId: selectedPatientId,
      pharmacyID: isPrescriptionGenerated ? sendToPharmacyID : EMPTY_GUID,
      drugs: Drugs,
      base64Image: base64Image,
      originPharmacyId: currentPharmacyId,
      companyID: company?.companyID,
    };
    const prescriptionCategory = selectedPrescriptionCategory;
    const payload = {
      prescriptionDetails: prescriptionModel,
      isPrescriptionGenerated: isPrescriptionGenerated,
      consultationID: consultationId,
      prescriptionCategory:
        prescriptionCategory === "private"
          ? 1
          : prescriptionCategory === "pgd"
          ? 2
          : 3,
    };
    const handlePrescriptionSaved = async () => {
      setIncrementPrescriptionCompleted(true);

      trackFirstTimeIntercomEvent({
        eventName: "prescription_created_first_time",
        user: {
          id: currentUser.emailAddress,
          email: currentUser.emailAddress,
          name: currentUser.fullName,
        },
        properties: {
          source: "consultation_quick_actions_prescription",
        },
      });

      try {
        trackServiceBilling({
          companyId,
          consultationId,
          pharmacyId: currentPharmacyId,
          actiontype: "prescription",
        });
      } catch (err) {
        console.error("Error calling billing tracker for prescription:", err);
      }

      await getPatientMedications(selectedPatientId);
    };

    addPrescription(payload).then(async (response) => {
      if (response === true) {
        await handlePrescriptionSaved();
      } else {
        console.error("add-prescription-data failed:", response?.message);
        showPrescriptionSendError({
          isDarkMode: darkMode,
          onRetry: async () => {
            const retryResponse = await addPrescription({
              ...payload,
              isRetry: true,
            });
            if (retryResponse === true) {
              await handlePrescriptionSaved();
              return { success: true };
            }
            console.error(
              "add-prescription-data retry failed:",
              retryResponse?.message,
            );
            return { success: false, message: retryResponse?.message };
          },
        });
      }
    });
    setPrescriptionId(newPrescriptionId);
    if (!skipActionSave) {
      handleActionSave(base64Image, payload);
    }
    handleRemoveClick();
  };

  const previewPrescription = async () => {
    setIsPreviewLoading(true);
    const Drugs = dynamicPrescriptions?.map((item) => ({
      prescriptionItemId: item.prescribedItemId,
      drugName: item?.value,
      strength: item?.strength,
      medicationType: item?.medicationType,
      quantity: Number(item?.quantity),
      dose: item?.dose,
      note: item?.notes,
    }));
    const result = await getHtmlData(Drugs, true);
  };

  const getHtmlData = async (Drugs, isPreview) => {
    const contactDetails = await getPharmacyContactDetails(
      sendToPharmacyID !== "" ? sendToPharmacyID : currentPharmacyId,
    );
    const selectedPatientAllergies =
      selectedPatientDetails?.allergies?.length > 0
        ? selectedPatientDetails?.allergies
            ?.map((a) => a.allergyName)
            .join(", ") + ","
        : null;
    const htmlData = {
      prescriptionDate: new Date().toLocaleDateString(),
      pharmacistRegInfo: "12/04/1987",
      prescriptionLink: appointmentApp,
      patientDetails: {
        title: selectedPatientDetails?.title || "",
        name: `${selectedPatientDetails?.foreName || ""}${
          selectedPatientDetails?.middleName
            ? " " + selectedPatientDetails.middleName
            : ""
        } ${selectedPatientDetails?.surName || ""}`.trim(),
        address: selectedPatientDetails?.address || "",
        dob: selectedPatientDetails?.dob
          ? new Date(selectedPatientDetails.dob).toLocaleDateString("en-GB")
          : "",
        phone:
          selectedPatientDetails?.contacts !== null
            ? selectedPatientDetails?.contacts[0]?.value || ""
            : "",
        email: selectedPatientDetails?.email,
        allergies: selectedPatientAllergies,
      },
      prescriptionInfo: Drugs,
      paymentInfo: "£21.54",
      authorizationCode: Math.floor(100000 + Math.random() * 900000).toString(),
      pharmacistInstructions: [
        "Visit tvrx.co.uk",
        "Check ePrescription is valid for dispensing by entering the authorisation code",
        "If ePrescription is valid, dispense to the patient and press 'Mark as Dispensed'",
        "If ePrescription is invalid or has expired, do not dispense to patient",
      ],
      pharmacyDetails: currentPharmacy,
      userDetails: currentUser,
      pharmacyContactDetails: contactDetails,
      company: company,
      approvedPharmacies: [
        `${currentPharmacy?.teamPharmacyName ??
          ""} ${currentPharmacy?.teamPharmacyAddress ?? ""}`,
      ],
      signature: "",
      prescriptionType: selectedPrescriptionCategory, // Add prescription type to preview data
    };

    try {
      const result = await generatePrescriptionImageBase64({ data: htmlData });
      if (!result) {
        console.error("Failed to generate prescription image");
        return;
      }

      const { base64Image } = result;
      setPrescriptionImageBase64(base64Image);
      if (isPreview === true) {
        setShowPrescriptionPreviewModal(true);
      }
      return result;
    } catch (error) {
      console.error("Error generating preview:", error);
    } finally {
      setIsPreviewLoading(false); // Stop loading
    }
  };

  const getItemErrors = (item) => {
    if (!showErrors) return {};

    try {
      prescriptionItemSchema.validateSync(item, { abortEarly: false });
      return {};
    } catch (err) {
      const errors = {};
      err.inner.forEach((error) => {
        errors[error.path] = error.message;
      });
      return errors;
    }
  };

  const { onboardingVerificationState } = useTitanApp();
  const { pharmacyContactDetails } = pharmacyServiceState;
  const { userCertificate } = onboardingVerificationState;
  const [showPIN, setShowPIN] = React.useState(false);
  const [prescriptionPIN, setPrescriptionPIN] = React.useState("");
  const [signatureError, setSignatureError] = React.useState("");
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [isDigitallysigned, setIsDigitallyigned] = React.useState(false);
  const {
    handleVerifyPinForPrescriptionApproval,
    handleUpdatePrescription,
  } = usePrescriptionData();
  const handleBackToSelection = () => {
    setWorkflowStep(WORKFLOW_STEPS.SELECTION);
    setPrescriptionPIN("");
    setSignatureError("");
    setIsDigitallyigned(false);
  };
  const handleDigitalSignature = async () => {
    if (isDisabled) return;
    setSignatureError("");
    if (!prescriptionPIN) {
      setSignatureError("PIN is required for digital signature");
      return;
    }

    if (prescriptionPIN.length !== 4) {
      setSignatureError("PIN must be exactly 4 digits");
      return;
    }

    if (!/^\d+$/.test(prescriptionPIN)) {
      setSignatureError("PIN must contain only numbers");
      return;
    }
    setIsProcessing(true);
    const result = await handleVerifyPinForPrescriptionApproval(
      prescriptionPIN,
    );
    if (result === null || result === "False") {
      setIsProcessing(false);
      setSignatureError("PIN verification failed. Please try again");
      return;
    }
    if (prescriptionType === 2) {
      await handleGenerateForApproval();
    } else {
      setTimeout(() => {
        setIsDigitallyigned(true);
        setWorkflowStep(WORKFLOW_STEPS.PHARMACY);
        setIsProcessing(false);
      }, 1000);
    }
  };

  return (
    <div className="pc-actions-modal-wrapper">
      <div className="pc-actions-modal-header">
        <h3>
          {isDisabled ? `${supplyNounTitle} ` : `Create ${supplyNounTitle}`}
        </h3>
        <IoClose
          className="close-icon"
          onClick={handleRemoveClick}
          color={darkMode ? "#f2f2f2" : "#222"}
        />
      </div>
      <div className="pc-actions-modal-content two-cols">
        <div className="left">
          <div className="prescription-items-wrapper">
            {dynamicPrescriptions?.map((item, index) => {
              const errors = getItemErrors(item);
              return (
                <div key={item?.id} className="prescription-internal-content">
                  {dynamicPrescriptions.length > 1 && (
                    <BsTrash3
                      className="trash-icon"
                      color={darkMode ? "#f2f2f2" : "#222"}
                      onClick={() => removePrescription(item?.id)}
                      size={14}
                    />
                  )}
                  <div className="index">Item {index + 1}</div>
                  <div className="fields">
                    {!isDisabled ? (
                      <div className="pc-modal-input-field med-field">
                        <label>Medication</label>
                        <SearchableDropdown
                          key={`dropdown-${item?.id}`}
                          className={`searchable-dropdown ${
                            errors?.value ? "error" : ""
                          }`}
                          searchType="drug"
                          label="name"
                          id="id"
                          selectedVal={
                            item?.prescribedItemId &&
                            !item?.isDescriptionFetched
                              ? ""
                              : item?.value
                          }
                          handleChange={(val) =>
                            handleChange(item?.id, "value", val, item)
                          }
                          maxLength={500}
                        />
                        {errors?.value && (
                          <div className="error-message">{errors?.value}</div>
                        )}
                      </div>
                    ) : (
                      <div className="pc-modal-input-field med-field">
                        <label>Medication</label>
                        <input
                          className={`template_input text_input_field ${
                            errors?.dose ? "error" : ""
                          }`}
                          type="text"
                          placeholder="e.g. Take 1 tablet twice daily"
                          value={
                            item?.prescribedItemId &&
                            !item?.isDescriptionFetched
                              ? ""
                              : item?.value
                          }
                          autoComplete="off"
                          isDisabled={isDisabled}
                          inputProps={{ maxLength: 5000 }}
                        />
                        {errors?.value && (
                          <div className="error-message">{errors?.value}</div>
                        )}
                      </div>
                    )}

                    <div className="mt-5 pc-modal-input-field">
                      <label>Dosage</label>
                      <textarea
                        rows={4}
                        disabled={isDisabled}
                        placeholder="e.g. Take 1 tablet twice daily"
                        maxLength={500}
                        value={item?.dose}
                        onChange={(e) =>
                          handleChange(item?.id, "dose", e.target.value, null)
                        }
                        className="
                          text-sm
                          w-full
                          rounded-[10px]
                          p-2
                          border-[var(--global-tabbed-content-panel-borders)]
                          border
                          text-[var(--para-clr)]
                          bg-[var(--grey-222)]
                          whitespace-pre-wrap
                          placeholder:text-sm
                          resize-y
                          break-words
                          focus:ring-1
                          focus:outline-none
                          disabled:opacity-50
                          focus:ring-[var(--global-tabbed-content-panel-borders)]
                          
                          disabled:cursor-not-allowed"
                      />

                      <div className="text-right text-xs text-[var(--para-clr)] mt-1">
                        {item?.dose?.length || 0}/500
                      </div>
                      {(item?.dose?.length || 0) >= 500 && (
                        <div className="error-message">
                          {charLimitMessage(500)}
                        </div>
                      )}

                      {errors?.dose && (
                        <div className="error-message">{errors?.dose}</div>
                      )}
                    </div>
                    <div className="mt-5 pc-modal-input-field">
                      <label>Quantity</label>
                      <input
                        className={`template_input text_input_field ${
                          errors?.quantity ? "error" : ""
                        }`}
                        key={`quantity-${item?.id}`}
                        type="number"
                        placeholder="e.g. 30 tablets, 100ml"
                        value={item?.quantity}
                        isDisabled={isDisabled}
                        min="0"
                        step="1"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === ".") {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (Number(value) < 0) return;
                          handleChange(item?.id, "quantity", value, null);
                        }}
                        inputProps={{ maxLength: 5000 }}
                      />
                      {errors?.quantity && (
                        <div className="error-message">{errors?.quantity}</div>
                      )}
                    </div>

                    <div className="mt-5 pc-modal-input-field">
                      <label>Additional Notes</label>
                      <textarea
                        rows={5}
                        disabled={isDisabled}
                        placeholder="Add any extra information for the patient"
                        maxLength={1000}
                        value={item?.notes}
                        onChange={(e) =>
                          handleChange(item?.id, "notes", e.target.value, null)
                        }
                        className="
                          w-full
                          text-sm
                          p-2
                          rounded-[10px]
                          border
                          border-[var(--global-tabbed-content-panel-borders)]
                          bg-[var(--grey-222)]
                          text-[var(--para-clr)]
                          placeholder:text-sm
                          whitespace-pre-wrap
                          break-words
                          resize-y
                          focus:outline-none
                          focus:ring-1
                          focus:ring-[var(--global-tabbed-content-panel-borders)]
                          disabled:opacity-50
                          disabled:cursor-not-allowed
                         "
                      />

                      <div className="text-right text-xs text-[var(--para-clr)] mt-1">
                        {item?.notes?.length || 0}/1000
                      </div>
                      {(item?.notes?.length || 0) >= 1000 && (
                        <div className="error-message">
                          {charLimitMessage(1000)}
                        </div>
                      )}
                      {errors?.notes && (
                        <div className="error-message">{errors?.notes}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {!isDigitallysigned && (
            <div
              className="add-template-btn"
              onClick={() => {
                if (isDisabled) return;
                addPrescriptionItems();
              }}
            >
              <GoPlus className="icons-pointer" fill="#999999" size={16} />
              Add more
            </div>
          )}
        </div>
        <div className="right">
          {workflowStep === WORKFLOW_STEPS.SELECTION && (
            <div className="prescription-selection-container">
              <h3 className="prescription-selection-title">
                {supplyNounTitle} Options
              </h3>

              {/* Prescription Category Selection */}
              <div className="prescription-category-section">
                <CustomPrescriptionTypeDropdown
                  selectedPrescriptionCategory={selectedPrescriptionCategory}
                  setSelectedPrescriptionCategory={
                    setSelectedPrescriptionCategory
                  }
                  setPrescriptionError={setPrescriptionError}
                  isDisabled={isDisabled}
                />
                {prescriptionError && (
                  <div className="error-message">{prescriptionError}</div>
                )}
              </div>

              {/* Processing Method Selection */}
              <div className="prescription-method-section">
                <label className="prescription-method-label">
                  Choose how to process this {supplyNoun}:
                </label>

                <div className="prescription-method-buttons">
                  <button
                    onClick={async () => {
                      await getUserCertificate();
                      setPrescriptionType(1);
                    }}
                    className={`prescription-method-btn ${
                      prescriptionType === 1
                        ? "prescription-method-btn--active"
                        : ""
                    }`}
                    disabled={userDigitalCertificate === null}
                  >
                    <div>
                      <div className="prescription-method-btn__title">
                        Generate & Issue {supplyNounTitle}
                      </div>
                      <div className="prescription-method-btn__description">
                        Immediately generate and issue the {supplyNoun}
                      </div>
                    </div>
                  </button>

                  {/* this button will add in future */}
                  <button
                    onClick={() => setPrescriptionType(2)}
                    className={`prescription-method-btn ${
                      prescriptionType === 2
                        ? "prescription-method-btn--active"
                        : ""
                    }`}
                  >
                    <div>
                      <div className="prescription-method-btn__title">
                        Generate {supplyNounTitle} for Approval
                      </div>
                      <div className="prescription-method-btn__description">
                        Submit {supplyNoun} for pharmacist approval
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="prescription-actions">
                <button
                  onClick={() => {
                    if (isDisabled) return;
                    handleRemoveClick();
                  }}
                  className="prescription-cancel-btn"
                >
                  Cancel
                </button>

                {prescriptionType === 1 && (
                  <button
                    onClick={handleProceedWithIssue}
                    className="prescription-continue-btn"
                    disabled={isContinueDisabled || isDisabled}
                  >
                    Continue
                  </button>
                )}

                {prescriptionType === 2 && (
                  <button
                    onClick={handleGenerateForApproval}
                    className="prescription-approval-btn"
                    disabled={
                      isContinueDisabled ||
                      isDisabled ||
                      isGenerateForApprovalLoading
                    }
                  >
                    {isGenerateForApprovalLoading
                      ? "Processing..."
                      : "Submit for Approval"}
                  </button>
                )}
              </div>
            </div>
          )}
          {workflowStep === WORKFLOW_STEPS.PHARMACY && (
            <>
              <div className="index">Select Pharmacy</div>
              <div>
                <SendToPharmacy
                  setSendToPharmacyID={setSendToPharmacyID}
                  setSendToPharmacy={setSendToPharmacy}
                  setPharmacyError={setPharmacyError}
                  isDisabled={isDisabled}
                  setButtonsDisabled={setButtonsDisabled}
                />

                {pharmacyError && (
                  <div className="error-message">{pharmacyError}</div>
                )}
              </div>
              <div className="action-buttons">
                <button
                  className="cancel"
                  onClick={() => setWorkflowStep(WORKFLOW_STEPS.SELECTION)}
                >
                  Back
                </button>
                <button
                  className="prescription-sendToPharmacy-btn"
                  disabled={buttonsDisabled}
                  onClick={async (e) => {
                    if (buttonsDisabled) {
                      e.preventDefault();
                      return;
                    }
                    await savePrescription(true, false);
                  }}
                >
                  Send to pharmacy
                </button>
                <button
                  className="preview-btn"
                  onClick={previewPrescription}
                  disabled={isPreviewLoading}
                  isdisable={isDisabled}
                >
                  {isPreviewLoading
                    ? "Processing..."
                    : `Preview ${supplyNounTitle}`}
                </button>
              </div>
            </>
          )}
          {workflowStep === WORKFLOW_STEPS.SUCCESS && (
            <div className="prescription-selection-container">
              <div className="prescription-status-screen">
                <h3 className="prescription-success-title">Success!</h3>
                <p className="prescription-status-text">
                  Your {supplyNoun} has been submitted for approval.
                </p>
                <button
                  onClick={() => {
                    // "Start New" loops back to the selection step without
                    // closing the modal (handleRemoveClick never runs), so
                    // selectedPrescriptionCategory would otherwise still be
                    // "pgd" from the just-finished AHT item even after the
                    // user picks a different category for the next one.
                    setSelectedPrescriptionCategory("");
                    setWorkflowStep(WORKFLOW_STEPS.SELECTION);
                  }}
                  className="prescription-new-btn"
                >
                  Start New {supplyNounTitle}
                </button>
              </div>
            </div>
          )}

          {workflowStep === WORKFLOW_STEPS.SIGNING && (
            <>
              <h3
                className={`mb-2 font-semibold ${
                  darkMode ? "text-gray-200" : "text-gray-900"
                }`}
              >
                Digital Signature Required
              </h3>

              <div className="space-y-4">
                {/* Certificate Information */}
                <div
                  className={`p-4 rounded-lg ${
                    darkMode
                      ? "bg-green-900/20 border border-green-700"
                      : "bg-green-50 border border-green-200"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <Shield className="w-5 h-5 text-green-600" />
                    <div>
                      <h4
                        className={`font-medium ${
                          darkMode ? "text-green-300" : "text-green-900"
                        }`}
                      >
                        Active Certificate
                      </h4>
                      <p className="text-sm text-green-700">
                        {currentUser?.fullName || ""}
                      </p>
                    </div>
                    <div className="ml-auto">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          darkMode
                            ? "bg-green-900/40 text-green-300"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </span>
                    </div>
                  </div>

                  <div
                    className={`text-xs space-y-1 ${
                      darkMode ? "text-green-400" : "text-green-600"
                    }`}
                  >
                    <p>
                      <span className="font-medium">Serial:</span>{" "}
                      {userCertificate?.serialNumber || ""}
                    </p>
                    <p>
                      <span className="font-medium">Expires:</span>{" "}
                      {new Date(
                        userCertificate?.expiryDate || "",
                      ).toLocaleDateString("en-GB")}
                    </p>
                    <p>
                      <span className="font-medium">Issuer:</span>{" "}
                      {userCertificate?.issuer || ""}
                    </p>
                    <p>
                      <span className="font-medium">Purpose:</span>{" "}
                      {supplyNounTitle} Authorisation
                    </p>
                  </div>
                </div>

                {/* PIN Input */}
                <div className="space-y-3">
                  <Label htmlFor="prescription-pin">
                    <Hash className="inline w-4 h-4 mr-2" />
                    Enter your 4-digit PIN to authorise this {supplyNoun}:
                  </Label>

                  <div className="relative">
                    <Input
                      id="prescription-pin"
                      type={showPIN ? "text" : "password"}
                      value={prescriptionPIN}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 4);
                        setPrescriptionPIN(value);
                        setSignatureError("");
                      }}
                      placeholder="Enter 4-digit PIN"
                      maxLength={4}
                      className={`${darkMode ? "bg-black" : "bg-white"} ${
                        signatureError ? "border-red-500" : ""
                      } text-center text-lg tracking-widest`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPIN(!showPIN)}
                    >
                      {showPIN ? (
                        <EyeOff className="w-4 h-4 text-gray-400" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-400" />
                      )}
                    </Button>
                  </div>

                  {signatureError && (
                    <p
                      className={`text-sm flex items-center gap-2 ${
                        darkMode ? "text-red-400" : "text-red-600"
                      }`}
                    >
                      <AlertTriangle className="w-4 h-4" />
                      {signatureError}
                    </p>
                  )}
                </div>

                {/* Security Notice */}
                <div
                  className={`p-3 rounded-lg ${
                    darkMode
                      ? "bg-amber-900/20 border border-amber-700"
                      : "bg-amber-50 border border-amber-200"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex items-start gap-2">
                      <Key className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div
                        className={`text-xs ${
                          darkMode ? "text-amber-300" : "text-amber-800"
                        }`}
                      >
                        <p className="mb-1 font-medium">
                          {supplyNounTitle} Authorisation
                        </p>
                        <p>
                          By entering your PIN, you are digitally signing this{" "}
                          {supplyNoun} with your active certificate and taking
                          full clinical responsibility for its accuracy and
                          appropriateness.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div
                className={`space-y-3 pt-4 ${
                  darkMode
                    ? "border-t border-gray-700"
                    : "border-t border-gray-200"
                }`}
              >
                <Button
                  variant="outline"
                  onClick={handleBackToSelection}
                  className="w-full"
                  disabled={isProcessing}
                >
                  Back
                </Button>
                <Button
                  onClick={handleDigitalSignature}
                  disabled={
                    isProcessing ||
                    !prescriptionPIN ||
                    prescriptionPIN.length !== 4
                  }
                  className="!mt-0 w-full !bg-green-600 text-white hover:!bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? "Verifying PIN..." : "Authorise with PIN"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrescriptionCardItems;
